import { hasAny, nonEmptyBullets, fmtRange, getSectionMarkerAttrs } from "./utils";
import { contactItems, ContactBit } from "./ContactBits";

export default function ExecutiveTemplate({ resume }) {
  const { personal, summary, experience, education, skills, projects, certifications, languages } = resume;
  return (
    <div
      style={{
        fontFamily: "'IBM Plex Sans', sans-serif",
        color: "#0a0a0a",
        background: "#ffffff",
        padding: "48px 56px",
        fontSize: 10,
        lineHeight: 1.5,
        width: "100%",
        height: "100%",
        boxSizing: "border-box",
      }}
    >
      <header style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 20, marginBottom: 18, paddingBottom: 12, borderBottom: "1px solid rgba(0,0,0,0.15)" }}>
        <div>
          <div data-resume-h1 style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: 24, letterSpacing: "-0.01em", lineHeight: 1 }}>
            {personal.fullName || "Your Name"}
          </div>
          {personal.title && (
            <div style={{ fontSize: 11, color: "#525252", marginTop: 4 }}>{personal.title}</div>
          )}
        </div>
        <div data-resume-contact="true" style={{ fontSize: 9, color: "#525252", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
          {contactItems(personal).map((it) => (
            <ContactBit key={it.key} Icon={it.Icon} value={it.value} size={9.5} color="#525252" />
          ))}
        </div>
      </header>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 24 }}>
        <div>
          {summary && (
            <Sect title="Professional Summary">
              <div>{summary}</div>
            </Sect>
          )}

          {skills?.length > 0 && (
            <Sect title="Core Skills">
              <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                {skills.map((s, i) => (
                  <li key={`${s}-${i}`} style={{ marginBottom: 2, paddingLeft: 8, position: "relative" }}>
                    <span style={{ position: "absolute", left: 0, top: 6, width: 3, height: 3, background: "#2400FF" }} />
                    {s}
                  </li>
                ))}
              </ul>
            </Sect>
          )}

          {hasAny(education, ["school", "degree"]) && (
            <Sect title="Education">
              {education.map((ed, i) => (ed.school || ed.degree) && (
                <div key={ed.id || i} style={{ marginBottom: 6 }}>
                  <div style={{ fontWeight: 600 }}>{ed.school}</div>
                  <div style={{ color: "#525252" }}>{ed.degree}{ed.field ? `, ${ed.field}` : ""}</div>
                  <div style={{ color: "#737373", fontSize: 9 }}>{fmtRange(ed.startDate, ed.endDate)}</div>
                  {ed.details && <div style={{ color: "#525252", fontSize: 9.5, marginTop: 2 }}>{ed.details}</div>}
                </div>
              ))}
            </Sect>
          )}

          {hasAny(certifications, ["name"]) && (
            <Sect title="Certifications">
              {certifications.map((c, i) => c.name && (
                <div key={c.id || i} style={{ marginBottom: 4 }}>
                  <div style={{ fontWeight: 600 }}>{c.name}</div>
                  <div style={{ color: "#525252", fontSize: 9.5 }}>{c.issuer}{c.date ? ` · ${c.date}` : ""}</div>
                </div>
              ))}
            </Sect>
          )}

          {languages?.some((l) => l.name?.trim()) && (
            <Sect title="Languages">
              {languages.map((l) => l.name?.trim() && (
                <div key={l.id} style={{ marginBottom: 3 }}>
                  <span style={{ fontWeight: 600 }}>{l.name}</span>
                  {l.level && <span style={{ color: "#525252" }}> — {l.level}</span>}
                </div>
              ))}
            </Sect>
          )}
        </div>

        <div>
          {hasAny(experience, ["company", "role"]) && (
            <Sect title="Professional Experience">
              {experience.map((e, i) => {
                const bullets = nonEmptyBullets(e.bullets);
                if (!e.company && !e.role && bullets.length === 0) return null;
                return (
                  <div key={e.id || i} data-page-item="true" style={{ marginBottom: 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                      <div data-resume-company style={{ fontWeight: 700, fontSize: 11 }}>{e.company}</div>
                      <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 8.5, color: "#737373" }}><span data-resume-date>{fmtRange(e.startDate, e.endDate)}</span></div>
                    </div>
                    <div style={{ color: "#404040", fontStyle: "italic" }}><span data-resume-jobtitle>{e.role}</span>{e.location ? <> · <span data-resume-location>{e.location}</span></> : ""}</div>
                    {bullets.length > 0 && (
                      <ul data-resume-workdesc style={{ margin: "4px 0 0 14px", padding: 0 }}>
                        {bullets.map((b, bi) => (
                          <li key={`${e.id || i}-b-${bi}`} style={{ marginBottom: 2 }}>{b}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </Sect>
          )}

          {hasAny(projects, ["name"]) && (
            <Sect title="Selected Projects">
              {projects.map((p, i) => p.name && (
                <div key={p.id || i} style={{ marginBottom: 8 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                    <div data-resume-projectname style={{ fontWeight: 700 }}>{p.name}</div>
                    <div style={{ fontSize: 9, color: "#737373" }}>{p.tech}</div>
                  </div>
                  {p.link && <div style={{ fontSize: 9, color: "#2400FF" }}>{p.link}</div>}
                  {p.description && <div style={{ marginTop: 2 }}>{p.description}</div>}
                </div>
              ))}
            </Sect>
          )}
        </div>
      </div>
    </div>
  );
}

function Sect({ title, children }) {
  return (
    <section {...getSectionMarkerAttrs(title)} style={{ marginBottom: 14 }}>
      <div data-resume-h2 style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 8.5, fontWeight: 700, letterSpacing: "0.22em", textTransform: "uppercase", color: "#2400FF", marginBottom: 6 }}>
        {title}
      </div>
      {children}
    </section>
  );
}
